import React, { useCallback, useEffect, useMemo } from 'react'
import { useNavigate } from 'react-router'
import { useEvent } from 'react-use-event-hook'
import { useOnJoinChannel } from '@components/AllChannelsList/AllChannelsList'
import { ChannelDiscrepancyChecker } from '@components/ChannelDiscrepancyChecker/ChannelDiscrepancyChecker'
import { Panel } from '@components/Panel/Panel'
import { Membership } from '@towns-protocol/sdk'
import { Box, Button, Icon, Pill, Stack, Text, TextField } from '@ui'
import fuzzysort from 'fuzzysort'
import { useChannelEntitlements } from 'hooks/useChannelEntitlements'
import { useChannelIdFromPathname } from 'hooks/useChannelIdFromPathname'
import { useLeaveChannel } from 'hooks/useLeaveChannel'
import { useSortedChannels } from 'hooks/useSortedChannels'
import { useSpaceChannels } from 'hooks/useSpaceChannels'
import { useUnseenChannelIds } from 'hooks/useUnseenChannelIdsCount'
import { PATHS } from 'routes'
import { ButtonSpinner } from 'ui/components/Spinner/ButtonSpinner'
import { addressFromSpaceId } from 'ui/utils/utils'
import {
    Channel,
    ChannelContextProvider,
    SpaceData,
    useMyMemberships,
    useSpaceData,
} from 'use-towns-client'

export const BrowseChannelsPanel = ({ onClose }: { onClose?: () => void }) => {
    const space = useSpaceData()
    const channels = useSpaceChannels()
    const [searchText, setSearchText] = React.useState('')

    const { unseenChannelIds, markChannelsAsSeen } = useUnseenChannelIds()

    useEffect(() => {
        return () => {
            markChannelsAsSeen()
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const myMemberships = useMyMemberships()
    const navigate = useNavigate()
    const { leaveChannel } = useLeaveChannel()
    const currentChannelId = useChannelIdFromPathname()

    const { sortedFavoriteChannels, sortedSpaceChannels } = useSortedChannels({
        spaceId: space?.id,
        currentRouteId: currentChannelId,
    })

    const onTextFieldChanged = useCallback(
        (event: React.ChangeEvent<HTMLInputElement>) => {
            setSearchText(event.target.value)
        },
        [setSearchText],
    )

    const onClosed = useEvent(() => {
        markChannelsAsSeen()
    })

    type ChannelWithUnseenStatus = Channel & { unseen: boolean }

    const filteredChannels: ChannelWithUnseenStatus[] = useMemo(() => {
        return fuzzysort
            .go(searchText, channels, { keys: ['label', 'search'], all: true })
            .map((m) => {
                return { ...m.obj, unseen: unseenChannelIds?.has(m.obj.id) }
            })
            .sort((a, b) => {
                if (a.unseen && !b.unseen) {
                    return -1
                }
                if (!a.unseen && b.unseen) {
                    return 1
                }
                return a.label.localeCompare(b.label)
            })
    }, [channels, searchText, unseenChannelIds])

    const handleChannelLeave = useCallback(
        async (channelId: string) => {
            if (!space) {
                return
            }

            await leaveChannel(channelId, space.id)

            if (channelId !== currentChannelId) {
                onClose?.()
                return
            }

            const spaceAddress = addressFromSpaceId(space.id)

            const favoriteCandidates = sortedFavoriteChannels.filter((c) => c.id !== channelId)
            const spaceCandidates = sortedSpaceChannels.filter((c) => c.id !== channelId)
            const next = favoriteCandidates[0]?.id ?? spaceCandidates[0]?.id

            if (next) {
                navigate(`/${PATHS.SPACES}/${spaceAddress}/${PATHS.CHANNELS}/${next}/`)
            } else {
                navigate(`/${PATHS.SPACES}/${spaceAddress}/`)
            }

            onClose?.()
        },
        [
            sortedFavoriteChannels,
            sortedSpaceChannels,
            space,
            navigate,
            leaveChannel,
            onClose,
            currentChannelId,
        ],
    )

    const onOpenChannel = useCallback(
        (channelId: string) => {
            onClose?.()
        },
        [onClose],
    )

    return (
        <Panel gap label="Channels" onClosed={onClosed}>
            <Box position="sticky" top="none">
                <TextField
                    background="level2"
                    placeholder="Search channels"
                    onChange={onTextFieldChanged}
                />
            </Box>
            <ChannelDiscrepancyChecker />
            {space && !space?.isLoadingChannels ? (
                channels.length > 0 ? (
                    <Stack scroll scrollbars gap>
                        {filteredChannels.map((channel) => (
                            <ChannelContextProvider key={channel.id} channelId={channel.id}>
                                <Stack>
                                    <BrowseChannelItem
                                        space={space}
                                        name={channel.label}
                                        topic={channel.topic}
                                        channelNetworkId={channel.id}
                                        isJoined={myMemberships[channel.id] === Membership.Join}
                                        showDot={channel.unseen}
                                        onOpenChannel={onOpenChannel}
                                        onLeaveChannel={handleChannelLeave}
                                    />
                                </Stack>
                            </ChannelContextProvider>
                        ))}
                    </Stack>
                ) : null
            ) : (
                <Stack centerContent padding gap="md">
                    <Text>Loading channels</Text>
                    <ButtonSpinner />
                </Stack>
            )}
        </Panel>
    )
}

const BrowseChannelItem = ({
    name,
    topic,
    channelNetworkId,
    space,
    showDot,
    isJoined,
    onOpenChannel,
    onLeaveChannel,
}: {
    name: string
    topic?: string
    channelNetworkId: string
    space: SpaceData
    showDot?: boolean
    isJoined?: boolean
    onOpenChannel?: (channelId: string) => void
    onLeaveChannel?: (channelId: string) => Promise<void>
}) => {
    const { hasSomeEntitlement } = useChannelEntitlements({
        spaceId: space?.id,
        channelId: channelNetworkId,
    })

    const { syncingSpace, joinFailed, setSyncingSpace, onClick } = useOnJoinChannel({
        channelId: channelNetworkId,
        space,
        isJoined,
    })

    useEffect(() => {
        if (!isJoined) {
            const timeout = setTimeout(() => {
                setSyncingSpace(false)
            }, 500)
            return () => {
                clearTimeout(timeout)
            }
        } else {
            setSyncingSpace(false)
        }
    }, [isJoined, setSyncingSpace])

    const onJoinClick = useEvent(async () => {
        if (isJoined && onLeaveChannel) {
            await onLeaveChannel(channelNetworkId)
        } else {
            await onClick()
        }
    })

    const onOpenChannelClick = useEvent(() => {
        onOpenChannel?.(channelNetworkId)
    })

    return (
        <Stack>
            <Stack horizontal justifyContent="spaceBetween" alignItems="center">
                <Stack
                    horizontal
                    grow
                    gap="sm"
                    overflow="hidden"
                    cursor="pointer"
                    alignItems="center"
                    onClick={onOpenChannelClick}
                >
                    <Icon
                        type={hasSomeEntitlement ? 'lock' : 'tag'}
                        padding="line"
                        background="level2"
                        size="square_lg"
                    />
                    <Stack gap="sm" overflow="hidden" padding="sm">
                        <Stack horizontal shrink={false} gap="sm" alignItems="center">
                            <Text truncate color="gray1" textAlign="left">
                                {name}{' '}
                            </Text>
                            {hasSomeEntitlement && (
                                <Pill
                                    paddingX={{ default: 'sm', mobile: 'xs' }}
                                    color="default"
                                    background="level3"
                                    height={{ default: 'input_sm', mobile: 'x2' }}
                                >
                                    <Text fontSize="sm">Gated</Text>
                                </Pill>
                            )}
                            {showDot && (
                                <Box
                                    shrink={false}
                                    width="x1"
                                    height="x1"
                                    background="accent"
                                    rounded="full"
                                />
                            )}
                        </Stack>
                        {topic && (
                            <Text color="gray2" size="sm">
                                {topic}
                            </Text>
                        )}
                    </Stack>
                </Stack>

                <Button
                    disabled={syncingSpace}
                    minWidth="100"
                    size="button_sm"
                    rounded="sm"
                    hoverEffect="none"
                    tone={syncingSpace ? 'level3' : isJoined ? 'level3' : 'cta1'}
                    onClick={onJoinClick}
                >
                    {syncingSpace ? <ButtonSpinner /> : isJoined ? 'Leave' : 'Join'}
                </Button>
            </Stack>
            {joinFailed && (
                <Box maxWidth="350" paddingTop="sm">
                    <Text textAlign="center" color="gray2" size="sm">
                        You are unable to join either because you don&apos;t have permission or this
                        town has reached its capacity
                    </Text>
                </Box>
            )}
        </Stack>
    )
}
